import React from 'react';
import { registerWidget } from '@okapi/core';
import { getGlobalRouter } from '@okapi/router-react';
import { createStore } from 'store';
import { State } from 'store/types';
import { CookiesEvents, FETCH_MODEL, GlobalEvents } from 'actions';
import { initI18n } from 'utils/init-i18n';
import { Header } from '../../common';
import { HeaderLogicProps } from '../../../components';
import { trackingHelper } from 'utils/tracking-helper';
import { useRegions } from 'components/header/hooks';
import { HeaderModelData } from 'types/model';
import {
  getDomain,
  getDatePlusYear,
  getRMSessionCookie,
  RM_SESSION_COOKIE,
  getLoyalityProCookie,
  LOYALITY_TYPE_PRO,
  getDatePlusHalAnfHour,
  getRegion,
  DEFAULT_RU_REGION,
} from 'utils';
import { v4 as uuidv4 } from 'uuid';
import { getCustomerId } from 'selectors';
import { getLoyaltyAccountDataKey } from 'common/utils/saved-loyalty-account-data';
import { parseCookies } from 'utils/parse-cookies';
import { B2B_ORGANIZATION_ACTIONS } from 'actions/b2b-organization';
import { isB2BUserAuthedClient, tryReadCurrentOrganizationNumberFromCookie } from 'utils/b2b-utils';

registerWidget<State, HeaderLogicProps>(`${__NAME__}/header`, (state) => {
  const router = getGlobalRouter();
  const store = createStore();

  store.dispatch(GlobalEvents.setRequestParams, {
    orchestratorHost: state.orchestratorHost,
    orchestratorBackendHost: state.orchestratorBackendHost,
    orchestratorHostKz: state.orchestratorHostKz,
    useCredentials: state.useCredentials,
    apiKey: state.apiKey,
  });

  store.dispatch(GlobalEvents.setRequestID, { requestID: state.requestID });
  store.dispatch(GlobalEvents.setIsBot, state.isBot);
  store.dispatch(GlobalEvents.setLogoProUrl, state.logoProUrl ?? '');
  store.dispatch(GlobalEvents.setDeviceType, state.deviceType);
  store.dispatch(CookiesEvents.set, document.cookie);

  const domain = getDomain(window.location.hostname);
  const region = getRegion(window.location.hostname, domain);

  const isCanFetchOrganizations = region === DEFAULT_RU_REGION && isB2BUserAuthedClient();
  if (isCanFetchOrganizations) {
    store.dispatch(
      B2B_ORGANIZATION_ACTIONS.setCurrentOrganizationNumber,
      tryReadCurrentOrganizationNumberFromCookie(),
    );
    store.dispatch(B2B_ORGANIZATION_ACTIONS.init);
  }

  if (!getRMSessionCookie(document.cookie)) {
    store.dispatch(CookiesEvents.update, {
      key: RM_SESSION_COOKIE,
      value: uuidv4(),
      params: {
        path: '/',
        domain: `.${getDomain(window.location.hostname)}`,
        expires: getDatePlusYear(),
      },
    });
  }

  if (!getLoyalityProCookie(document.cookie)) {
    const parsedCookies = parseCookies(document.cookie);
    const customerNumber = getCustomerId(parsedCookies);

    if (customerNumber) {
      const data = localStorage.getItem(getLoyaltyAccountDataKey(customerNumber));
      let loyaltyType = '';

      if (data) {
        try {
          const parsedData = JSON.parse(data);
          if ('loyaltyTypePRO' in parsedData) {
            loyaltyType = String(parsedData.loyaltyTypePRO);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Invalid JSON in localStorage for loyalty data', error);
        }
      }

      store.dispatch(CookiesEvents.update, {
        key: LOYALITY_TYPE_PRO,
        value: loyaltyType,
        params: {
          path: '/',
          domain: `.${getDomain(window.location.hostname)}`,
          expires: getDatePlusHalAnfHour(),
        },
      });
    }
  }

  store.dispatch(GlobalEvents.setPathname, state.path ?? '/');
  store.dispatch(GlobalEvents.setHost, state.host);
  store.dispatch(FETCH_MODEL.success, state.model ?? undefined);
  initI18n(router.getPath(), state.host);

  trackingHelper.setOrchestratorHostAndApiKey(
    state.orchestratorHost,
    state.requestID,
    document.cookie,
    state.apiKey,
  );
  const { defaultRegion, currentRegion } = useRegions(state.model as HeaderModelData);
  trackingHelper.listenAddToCart(currentRegion?.regionId || defaultRegion.regionId);
  trackingHelper.listenSearch();

  return (remoteProps) => <Header router={router} store={store} {...remoteProps} />;
});
